import { ajvResolver } from '@hookform/resolvers/ajv';
import { Info as InfoIcon } from '@mui/icons-material';
import LoopIcon from '@mui/icons-material/Loop';
import { Alert, Box, Button, Grid, Stack, Typography } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { get as getObjectValue, omit } from 'lodash';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CustomButton } from 'src/components/button/Button';
import { Header } from 'src/components/header';
import {
  PublicationModal,
  SimpleDialog,
  SyncLocationsModal,
} from 'src/components/modal';
import { AssetUploadModal } from 'src/components/modal/assetUploadModal/AssetUploadModal';
import { Progress } from 'src/components/progress/Progress';
import { SettingsCategory } from 'src/components/settings';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
  UploadFileWidget,
} from 'src/components/widget';
import {
  ALLOWED_FILE_EXTENSIONS_OUTAGE,
  ASSET_UPLOAD_SUPPORTED_FILE_TYPES_OUTAGE,
  CURRENCY_POSITION_OPTIONS,
} from 'src/constants';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import {
  useGetAllAssets,
  useGetProjectConfig,
  useModal,
  useUpdateProjectConfigs,
  useUploadAsset,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import {
  CircularCurrencies,
  productDisclaimerPositionEnum,
  ProjectConfigInterface,
  ProjectConfigSchema,
} from 'src/interfaces';
import { useUserRole } from 'src/utils';
import { lang } from 'src/utils/language';

import {
  SubContainer,
  SubContent,
} from '../settingsSubNavbar/SettingsSubNavbar.styled';
import {
  generateOutageStrategyOptions,
  generateProjectConfigAssets,
} from './EditGlobalSettingsPage.helper';
import { CodAlert, FooterContainer } from './EditGlobalSettingsPage.styled';
import { GLOBAL_SETTINGS_LABELS } from './global_settings_labels.helper';
import { useEditGlobalSettingsPage } from './hooks/EditGlobalSettingsPage.hook';

export const EditGlobalSettingsPage = () => {
  const assetUploadModal = useModal();
  const {
    showPublicationModal,
    showSyncLocationsModal,
    onTogglePublicationModal,
    onToggleSyncLocationsModal,
  } = useEditGlobalSettingsPage();

  const [publicationTitle, setPublicationTitle] = useState<string | undefined>(
    undefined,
  );
  const [isPublishing, setIsPublishing] = useState(false);

  const updateProjectConfigs = useUpdateProjectConfigs();

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const uploadAsset = useUploadAsset();

  const { data: assets = [] } = useGetAllAssets();

  const { data: projectConfig, isLoading: isLoadingGlobalConfig } =
    useGetProjectConfig();

  const confirmGlobalConfigurations = useModal();

  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    reset,
    clearErrors,
  } = useForm<Partial<ProjectConfigInterface> & { uploadedAsset?: File }>({
    values: projectConfig,
    resolver: ajvResolver(
      ProjectConfigSchema as unknown as JSONSchemaType<ProjectConfigInterface>,
    ),
  });

  const onClickedConfirm = async (
    values: Partial<ProjectConfigInterface> & { uploadedAsset?: File },
  ) => {
    const dirtyFieldsArray = Object.keys(
      omit(dirtyFields, 'uploadedAsset'),
    ) as (keyof ProjectConfigInterface)[];

    const payload = {
      data: dirtyFieldsArray.map((id) => ({
        resource: COLLECTIONS.PROJECT_CONFIGS,
        method: 'PATCH',
        id,
        data: {
          value: values[id],
          asset_names: generateProjectConfigAssets(id, values[id]),
          narrative: narrativeGenerators[COLLECTIONS.PROJECT_CONFIGS][
            USER_ACTIONS.UPDATE
          ](getObjectValue(GLOBAL_SETTINGS_LABELS, id, id)),
        },
      })),
    };

    confirmGlobalConfigurations.onCloseModal();

    await updateProjectConfigs.mutateAsync(payload.data);

    onTogglePublicationModal();
  };

  const onClickedCancel = () => {
    reset();
  };

  const disabled =
    isLoadingGlobalConfig ||
    uploadAsset.isLoading ||
    updateProjectConfigs.isLoading;

  const showCurrencyEnabled = Boolean(watch('productPriceCurrency'));
  const uploadedAsset = watch('uploadedAsset');
  const { isViewOnly } = useUserRole();

  return (
    <form>
      <SubContainer>
        {isLoadingGlobalConfig ? (
          <Progress />
        ) : (
          <>
            <AssetUploadModal
              assetUploadModal={assetUploadModal}
              allowedFileExtension={ALLOWED_FILE_EXTENSIONS_OUTAGE}
              supportedFileType={ASSET_UPLOAD_SUPPORTED_FILE_TYPES_OUTAGE}
              setFieldValue={setValue}
            />
            <Header
              title={lang.headerGlobalConfigTitle}
              icon={<InfoIcon />}
              iconText={lang.headerGlobalConfigIconText}
              breadcrumbItems={[
                {
                  label: 'Home',
                  link: '/',
                },
                {
                  label: 'Configuration',
                },
              ]}
              actionBar={isViewOnly && <ViewOnlyAlert />}
            />

            {!isViewOnly && (
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Alert
                    severity="info"
                    action={
                      <Button
                        disabled={isPublishing}
                        variant="contained"
                        onClick={() => {
                          setPublicationTitle('Add publication');
                          onTogglePublicationModal();
                        }}
                        style={{
                          marginTop: '-4px',
                        }}>
                        {lang.publishAllChanges}
                      </Button>
                    }>
                    {lang.alertMessages.publishAllChanges}
                  </Alert>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Alert
                    severity="info"
                    action={
                      <Button
                        variant="contained"
                        onClick={() => onToggleSyncLocationsModal()}
                        style={{
                          marginTop: '-4px',
                        }}>
                        {lang.syncLocationsButton}
                      </Button>
                    }>
                    {lang.importLocationsText}
                  </Alert>
                </Grid>
              </Grid>
            )}

            <SubContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CodAlert>
                    <Alert severity="info" icon={false}>
                      {lang.cod_position_alert(projectConfig?.codPosition)}
                    </Alert>
                  </CodAlert>
                  <SettingsCategory title={lang.outageType}>
                    <SettingsCategory.Content>
                      <Controller
                        name="outageStrategy"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            disabled={disabled || isViewOnly}
                            label={lang.outageType}
                            selectOptions={generateOutageStrategyOptions()}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </SettingsCategory.Content>
                  </SettingsCategory>

                  <SettingsCategory title={lang.outageStyling}>
                    <SettingsCategory.Content title="Sold out styling">
                      <Controller
                        name="productPriceSoldoutSticker.textValue"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <InputWidget
                            {...field}
                            ref={ref}
                            disabled={disabled || isViewOnly}
                            value={field.value}
                            label="Sold out label"
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Stack gap="6px">
                        <Typography fontWeight="400">{lang.soldOut}</Typography>

                        <Stack direction="row" gap="20px" alignItems="end">
                          <Controller
                            name="productPriceSoldoutSticker.textColor"
                            control={control}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <ColorPickerWidget
                                {...field}
                                ref={ref}
                                disabled={disabled || isViewOnly}
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                hasError={!!fieldState.error}
                                error={fieldState.error?.message}
                              />
                            )}
                          />

                          <Controller
                            name="productPriceSoldoutSticker.backgroundColor"
                            control={control}
                            render={({
                              field: { ref, ...field },
                              fieldState,
                            }) => (
                              <ColorPickerWidget
                                {...field}
                                ref={ref}
                                disabled={disabled || isViewOnly}
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                hasError={!!fieldState.error}
                                error={fieldState.error?.message}
                                options={{
                                  backgroundColor: true,
                                }}
                              />
                            )}
                          />
                        </Stack>
                      </Stack>
                    </SettingsCategory.Content>

                    <SettingsCategory.Content title="Back soon styling">
                      <Controller
                        name="customizedOutageConfig.textValue"
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputWidget
                            {...field}
                            disabled={disabled || isViewOnly}
                            label="Back soon label"
                            hasError={!!fieldState.error?.message}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Stack gap="6px">
                        <Typography fontWeight="400">
                          {lang.backSoon}
                        </Typography>

                        <Stack direction="row" alignItems="end" gap="20px">
                          <Controller
                            name="customizedOutageConfig.color"
                            control={control}
                            render={({ field, fieldState }) => (
                              <ColorPickerWidget
                                {...field}
                                disabled={disabled || isViewOnly}
                                hasError={!!fieldState.error?.message}
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                          <Controller
                            name="customizedOutageConfig.fontSize"
                            control={control}
                            render={({ field, fieldState }) => (
                              <NumberInputWidget
                                {...field}
                                label="Font size"
                                options={{ suffix: 'px' }}
                                disabled={disabled || isViewOnly}
                                hasError={!!fieldState.error?.message}
                                error={fieldState.error?.message}
                              />
                            )}
                          />

                          <Controller
                            name="customizedOutageConfig.opacity"
                            control={control}
                            render={({ field, fieldState }) => (
                              <NumberInputWidget
                                {...field}
                                label="Text opacity"
                                options={{ suffix: '%' }}
                                disabled={disabled || isViewOnly}
                                hasError={!!fieldState.error?.message}
                                error={fieldState.error?.message}
                              />
                            )}
                          />

                          <Controller
                            name="customizedOutageConfig.strikethroughThickness"
                            control={control}
                            render={({ field, fieldState }) => (
                              <NumberInputWidget
                                {...field}
                                label="Strikethrough thickness"
                                options={{ suffix: 'px' }}
                                disabled={disabled || isViewOnly}
                                hasError={!!fieldState.error?.message}
                                error={fieldState.error?.message}
                              />
                            )}
                          />

                          <Controller
                            name="customizedOutageConfig.strikethroughColor"
                            control={control}
                            render={({ field, fieldState }) => (
                              <ColorPickerWidget
                                {...field}
                                disabled={disabled || isViewOnly}
                                hasError={!!fieldState.error?.message}
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                        </Stack>
                      </Stack>

                      <Controller
                        name="customizedOutageConfig.stamp.visible"
                        control={control}
                        render={({ field, fieldState }) => (
                          <SwitchButtonWidget
                            {...field}
                            label="Back soon sticker"
                            disabled={disabled || isViewOnly}
                            hasError={Boolean(fieldState.error?.message)}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Controller
                        name="customizedOutageConfig.stamp.url"
                        control={control}
                        render={({ field, fieldState }) => {
                          const value = uploadedAsset
                            ? URL.createObjectURL(uploadedAsset)
                            : assets.find(
                                (item) => item.fileName === field.value,
                              )?.thumbnailUrl;

                          return (
                            <Stack direction="row" alignItems="end" gap="20px">
                              <UploadFileWidget
                                {...field}
                                ref={fileUploadRef}
                                name=""
                                disabled={disabled || isViewOnly}
                                value={value}
                                onChange={(value) => {
                                  if (value instanceof File) {
                                    field.onChange(value.name);
                                    setValue('uploadedAsset', value);
                                  }
                                }}
                                assetUploadModal={assetUploadModal} // Pass the modal as a prop
                                hasError={Boolean(fieldState.error?.message)}
                                error={fieldState.error?.message}
                                options={{
                                  hint: 'Upload sticker',
                                  hintStyle: { fontSize: '12px' },
                                  iconStyle: {
                                    width: '20px',
                                    height: '20px',
                                  },
                                  imageStyle: {
                                    width: '76px',
                                    height: '76px',
                                  },
                                  fileTypes: 'image/png',
                                }}
                              />

                              {Boolean(value) && (
                                <Button
                                  sx={{ bottom: '10px' }}
                                  variant="outlined"
                                  startIcon={<LoopIcon />}
                                  disabled={disabled || isViewOnly}
                                  onClick={() => {
                                    assetUploadModal.onOpenModal();
                                  }}>
                                  {lang.replace}
                                </Button>
                              )}
                            </Stack>
                          );
                        }}
                      />

                      <Stack direction="row" gap="20px" alignItems="end">
                        <Controller
                          name="customizedOutageConfig.stamp.width"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label="Width"
                              options={{ suffix: 'px' }}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                            />
                          )}
                        />

                        <Controller
                          name="customizedOutageConfig.stamp.height"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label="Height"
                              options={{ suffix: 'px' }}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                            />
                          )}
                        />

                        <Controller
                          name="customizedOutageConfig.stamp.left"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label="Left position"
                              options={{ suffix: 'px' }}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                            />
                          )}
                        />

                        <Controller
                          name="customizedOutageConfig.stamp.top"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label="Top position"
                              options={{ suffix: 'px' }}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </Stack>
                    </SettingsCategory.Content>
                  </SettingsCategory>

                  <SettingsCategory
                    title={lang.calories}
                    icon={<InfoIcon />}
                    iconText={lang.caloriesIconText}>
                    <SettingsCategory.Content>
                      <Controller
                        name="nutrition.label"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                          <InputWidget
                            {...field}
                            ref={ref}
                            disabled={disabled || isViewOnly}
                            value={field.value}
                            label="Calorie label"
                            onChange={(value) => {
                              field.onChange(value);
                              clearErrors('nutrition');
                            }}
                            hasError={!!errors.nutrition}
                            error={
                              errors.nutrition?.message ===
                              'must match "then" schema'
                                ? 'Required'
                                : false
                            }
                          />
                        )}
                      />

                      <Controller
                        name="productPriceCalorieSeparator"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SwitchButtonWidget
                            {...field}
                            ref={ref}
                            label="Show pipe divider between price and calories"
                            disabled={disabled || isViewOnly}
                            value={field.value === '|'}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                            options={{
                              switchValueTrue: '|',
                              switchValueFalse: '',
                            }}
                          />
                        )}
                      />
                    </SettingsCategory.Content>
                  </SettingsCategory>
                </Grid>

                <Grid item xs={6}>
                  <SettingsCategory title="Currency and pricing settings">
                    <SettingsCategory.Content>
                      <Controller
                        name="productPriceCurrency"
                        control={control}
                        render={({ field }) => (
                          <SwitchButtonWidget
                            value={Boolean(field.value)}
                            label="Show currency symbol on prices"
                            disabled={disabled || isViewOnly}
                            onChange={(value) => {
                              field.onChange(
                                value ? CircularCurrencies[0].value : '',
                              );
                            }}
                          />
                        )}
                      />

                      <Controller
                        name="productPriceCurrencyPosition"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            label="Currency position"
                            selectOptions={CURRENCY_POSITION_OPTIONS}
                            disabled={
                              disabled || !showCurrencyEnabled || isViewOnly
                            }
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Controller
                        name="productPriceCurrency"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            value={field.value}
                            label="Currency"
                            selectOptions={CircularCurrencies.map(
                              (currency) => ({
                                label: currency.label,
                                value: currency.value,
                              }),
                            )}
                            disabled={
                              disabled || !showCurrencyEnabled || isViewOnly
                            }
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Controller
                        name="productPriceDecimalSeparator"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            disabled={disabled || isViewOnly}
                            value={field.value}
                            label="Pricing separator"
                            selectOptions={[
                              { label: '. - Dot', value: '.' },
                              { label: ', - Comma', value: ',' },
                            ]}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Controller
                        name="productPriceSeparator"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SwitchButtonWidget
                            {...field}
                            ref={ref}
                            label="Show pipe divider between products and prices"
                            disabled={disabled || isViewOnly}
                            value={field.value === '|'}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                            options={{
                              switchValueTrue: '|',
                              switchValueFalse: '',
                            }}
                          />
                        )}
                      />

                      <Controller
                        name="currencySpace"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SwitchButtonWidget
                            {...field}
                            ref={ref}
                            label={lang.currenySpaceLabel}
                            disabled={disabled || isViewOnly}
                            value={field.value ?? false}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Controller
                        name="showZeroPrices"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SwitchButtonWidget
                            {...field}
                            ref={ref}
                            label="Hide 0.00 pricing"
                            disabled={disabled || isViewOnly}
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </SettingsCategory.Content>
                  </SettingsCategory>

                  <SettingsCategory title="Product disclaimer">
                    <Grid>
                      <Controller
                        name="showGlobalProductDisclaimer.type"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            label="Disclaimer display location"
                            selectOptions={[
                              {
                                label: 'Product disclaimer',
                                value:
                                  productDisclaimerPositionEnum.withProductText,
                              },
                              {
                                label: 'Component disclaimer',
                                value:
                                  productDisclaimerPositionEnum.withComponentDisclaimer,
                              },
                            ]}
                            disabled={disabled || isViewOnly}
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error ? 'Required' : false}
                          />
                        )}
                      />
                    </Grid>

                    <Grid>
                      <Controller
                        name="showGlobalProductDisclaimer.style"
                        control={control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <SelectDropdownWidget
                            {...field}
                            ref={ref}
                            disabled={
                              disabled ||
                              isViewOnly ||
                              watch('showGlobalProductDisclaimer.type') ===
                                productDisclaimerPositionEnum.withComponentDisclaimer
                            }
                            value={field.value}
                            label="Style"
                            selectOptions={[
                              { label: 'Inline', value: 'INLINE' },
                              { label: 'Multi line', value: 'MULTILINE' },
                            ]}
                            onChange={(value) => field.onChange(value)}
                            hasError={!!fieldState.error}
                            error={fieldState.error ? 'Required' : false}
                          />
                        )}
                      />
                    </Grid>
                  </SettingsCategory>
                  <SettingsCategory title={lang.positionSetting.title}>
                    <Stack direction="row" gap="20px" alignItems="flex-start">
                      <Box flex={1}>
                        <Controller
                          name="idmbPositionNumber"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              data-testid="defaultDuration-input"
                              {...field}
                              label={lang.positionSetting.idmbPosition}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                              options={{ min: 1, max: 15, integer: true }}
                            />
                          )}
                        />
                      </Box>

                      <Box flex={1}>
                        <Controller
                          name="odmbPositionNumber"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label={lang.positionSetting.odmbPosition}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                              options={{ min: 1, max: 15, integer: true }}
                            />
                          )}
                        />
                      </Box>

                      <Box flex={1}>
                        <Controller
                          name="mcCafePositionNumber"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label={lang.positionSetting.mcCafePosition}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                              options={{ min: 1, max: 15, integer: true }}
                            />
                          )}
                        />
                      </Box>

                      <Box flex={1}>
                        <Controller
                          name="merchandiserPositionNumber"
                          control={control}
                          render={({ field, fieldState }) => (
                            <NumberInputWidget
                              {...field}
                              label={lang.positionSetting.merchandiserPosition}
                              disabled={disabled || isViewOnly}
                              hasError={!!fieldState.error}
                              error={fieldState.error?.message}
                              options={{ min: 1, max: 15, integer: true }}
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                  </SettingsCategory>
                </Grid>
              </Grid>
            </SubContent>
            {!isViewOnly && (
              <FooterContainer>
                <CustomButton
                  variant="text"
                  onClick={onClickedCancel}
                  disabled={disabled}>
                  Cancel
                </CustomButton>
                <Button
                  onClick={handleSubmit((value) => {
                    setPublicationTitle(undefined);
                    confirmGlobalConfigurations.onOpenModal(() =>
                      onClickedConfirm(value),
                    );
                  })}
                  disabled={disabled}>
                  Save
                </Button>
              </FooterContainer>
            )}
          </>
        )}
      </SubContainer>

      {showPublicationModal && (
        <PublicationModal
          title={publicationTitle}
          open={showPublicationModal}
          onClose={onTogglePublicationModal}
          publishMessage={lang.successfullyPublished}
          onPublished={setIsPublishing}
        />
      )}

      {showSyncLocationsModal && (
        <SyncLocationsModal
          open={showSyncLocationsModal}
          onClose={onToggleSyncLocationsModal}
        />
      )}

      <SimpleDialog
        open={confirmGlobalConfigurations.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.titleTextSave}
        subTitle={lang.subTitlePublishConfirm}
        onClose={confirmGlobalConfigurations.onCloseModal}
        onClickCancel={confirmGlobalConfigurations.onCloseModal}
        modalSize="sm"
        onClickConfirm={confirmGlobalConfigurations.customFn}
      />
    </form>
  );
};
